import * as React from "react";
import i18next from "i18next";
import { PopupBody, Popup, PopupHeader } from "../../Popups/Popup";
import { getMap } from "../GeoLiteMap";
import { Vector as VectorSource } from "ol/source";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMapContext } from "../../Contexts/MapContext";
import { Select } from "../../Elements/Select";
import { IGroup } from "../../Contexts/SearchContext";
import { PopupHeaderButton } from "../../Popups/PopupHeaderButton";
import { EventsKey } from "ol/events";
import { unByKey } from "ol/Observable";
import {
    addProfileData,
    createProfile,
    createProfileLayer,
    createTracks,
    multiLineToLine,
    removeDiacritics
} from "./elevationUtils";
import { getAllFeatures } from "../rest";
import "./ElevationPopup.scss";

export interface IOptGroup {
    value: string;
    label: string;
    group: string;
}

export const ElevationPopup = (): JSX.Element => {
    const { configA, elevationVisible, setElevationVisible, setElevationDisabled } = useMapContext();
    const [optionsInExtent, setOptionsInExtent] = useState<IGroup[]>([]);

    const emptyOption = { label: i18next.t("elevation.selectTrack"), value: "placeholder" };

    const targetRef = useRef<HTMLDivElement>();
    const sourceRef = useRef<VectorSource>(new VectorSource());
    const featuresSourceRef = useRef<VectorSource>(new VectorSource());
    const allOptionsRef = useRef<IGroup[]>();
    const selectedOptionRef = useRef<IGroup>(emptyOption);
    const mapScaleWatcher = useRef<EventsKey>();

    const map = getMap();

    useEffect(() => {
        allOptionsRef.current = createTracks(configA);
        fetchFeatures(allOptionsRef.current);

        mapScaleWatcher.current = map.on("moveend", onMoveEnd);

        return () => {
            if (mapScaleWatcher.current) {
                unByKey(mapScaleWatcher.current);
                mapScaleWatcher.current = null;
            }
        };
    }, []);

    useEffect(() => {
        elevationVisible ? addControl() : removeControl();
    }, [elevationVisible]);

    const onSelectTrack = useCallback(
        (e) => {
            const option = optionsInExtent.find((o) => o.label === e.target.value);

            updateProfile(option);
            selectedOptionRef.current = option;
        },
        [optionsInExtent]
    );

    const onMoveEnd = () => {
        validateScale();
        validateFeaturesInExtent();
    };

    const validateScale = () => {
        if ((document as any).mapScale > 100000) {
            setElevationVisible(false);
            setElevationDisabled(true);
        } else {
            setElevationDisabled(false);
        }
    };

    const validateFeaturesInExtent = () => {
        const viewExtent = map.getView().calculateExtent(map.getSize());
        const featuresInExtent = featuresSourceRef.current.getFeaturesInExtent(viewExtent);

        if (!featuresInExtent || !featuresInExtent.length) {
            setElevationVisible(false);
            setElevationDisabled(true);
            return;
        }
        const optionsInExt = allOptionsRef.current.filter((o) =>
            featuresInExtent.find((f) => f.get("TRACKTYPE") === removeDiacritics(o.label))
        );
        optionsInExt.unshift(emptyOption);
        if (selectedOptionRef.current.value === "placeholder") {
            setOptionsInExtent(optionsInExt);
            sourceRef.current.clear();
            updateProfile(selectedOptionRef.current);
            return;
        }
        if (!optionsInExt.find((o) => o.label === selectedOptionRef.current.label)) {
            sourceRef.current.clear();
            selectedOptionRef.current = emptyOption;
            updateProfile(selectedOptionRef.current);
        }
        setOptionsInExtent(optionsInExt);
    };

    const addControl = () => {
        map.addControl(createProfile(targetRef.current));
        map.addLayer(createProfileLayer(sourceRef.current));
    };

    const removeControl = () => {
        const profileControl = map
            .getControls()
            .getArray()
            .find((control: any) => control?.info);
        map.removeControl(profileControl);
        map.removeLayer(
            map
                .getLayers()
                .getArray()
                .find((l) => l.get("id") === "elevationProfileLayer")
        );
    };

    const updateProfile = (option) => {
        const viewExtent = map.getView().calculateExtent(map.getSize());
        const featureInViewExtent = featuresSourceRef.current
            .getFeaturesInExtent(viewExtent)
            .find((f) => option.value !== "placeholder" && f.get("TRACKTYPE") === removeDiacritics(option.label));

        addProfileData(map, targetRef.current, sourceRef.current, featureInViewExtent);
    };

    const fetchFeatures = async (trackOptions: IGroup[]) =>
        Promise.all(
            trackOptions.map(async (track: IGroup) => {
                if (track.value === "placeholder") {
                    return;
                }
                const rawFeat = await getAllFeatures(track.value, true);
                const parsedFeat = rawFeat.map((feature) => multiLineToLine(feature));

                featuresSourceRef.current.addFeatures(parsedFeat);
                validateScale();
                validateFeaturesInExtent();
            })
        );

    const onClose = () => {
        setElevationVisible(false);
        sourceRef.current.clear();
    };

    return !elevationVisible ? null : (
        <Popup id={"elevation-popup"} sameContainer={false} isMobile={false} className={"relative"}>
            <PopupHeader isMobile={false}>
                <div>{i18next.t("elevation.elevationProfile")}</div>
                <PopupHeaderButton iconClassName={"icon-geolite-close"} title={i18next.t("close")} onClick={onClose} />
            </PopupHeader>

            <PopupBody isMobile={false}>
                <div className="track-select">
                    {optionsInExtent?.length ? (
                        <Select
                            title={i18next.t("elevation.elevationProfile")}
                            config={{ field: "anything" }}
                            options={optionsInExtent}
                            defaultValue={"aaaa"}
                            onChange={onSelectTrack}
                        />
                    ) : null}
                </div>
                <div id="profile" ref={targetRef}></div>
            </PopupBody>
        </Popup>
    );
};
